import React, { useEffect, useRef, useState } from 'react';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import { Upload, Music, X, Eye, EyeOff, Maximize2, Minimize2 } from 'lucide-react';

/**
 * NotationSection component for displaying and editing MusicXML notation
 */
const NotationSection = ({ 
  notation, 
  onNotationChange, 
  sectionRef = '',
  title = '',
  onSectionRefChange,
  onTitleChange,
  isEditing = false,
  isPrintMode = false,
  minimalMode = false,  // Hide all UI except notation
  className = ''
}) => {
  const osmdContainerRef = useRef(null);
  const osmdInstanceRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadedFileRef = useRef(null); // Store the actual File object for MXL
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [musicXmlContent, setMusicXmlContent] = useState(notation || '');
  const [isMxlFile, setIsMxlFile] = useState(
    notation && typeof notation === 'string' && notation.startsWith('mxl:base64:')
  );
  const [localSectionRef, setLocalSectionRef] = useState(sectionRef || '');
  const [localTitle, setLocalTitle] = useState(title || '');

  // Add effect to handle visibility changes and ensure rendering
  useEffect(() => {
    if (osmdInstanceRef.current && isVisible && musicXmlContent) {
      // Use IntersectionObserver to detect when element becomes visible
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Element is visible, trigger render
              setTimeout(() => {
                if (osmdInstanceRef.current) {
                  try {
                    osmdInstanceRef.current.render();
                  } catch (err) {
                    console.debug('OSMD render on intersection:', err.message);
                  }
                }
              }, 100);
            }
          });
        },
        { threshold: 0.1 }
      );

      if (osmdContainerRef.current) {
        observer.observe(osmdContainerRef.current);
      }

      return () => {
        if (osmdContainerRef.current) {
          observer.unobserve(osmdContainerRef.current);
        }
      };
    }
  }, [isVisible, musicXmlContent]);

  // Initialize OSMD when component mounts or notation changes
  useEffect(() => {
    if (!osmdContainerRef.current || !musicXmlContent || !isVisible) {
      return;
    }

    const initializeOSMD = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Clean up previous instance
        if (osmdInstanceRef.current) {
          osmdInstanceRef.current.clear();
        }

        // Force container to have dimensions before initializing OSMD
        if (osmdContainerRef.current) {
          osmdContainerRef.current.style.minHeight = '200px';
          osmdContainerRef.current.style.width = '100%';
        }

        // Small delay to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100));

        // Create new OSMD instance
        const osmd = new OpenSheetMusicDisplay(osmdContainerRef.current, {
          backend: 'svg',
          drawTitle: false,  // Hide title from the XML
          drawComposer: false,  // Hide composer from the XML
          drawLyricist: false,  // Hide lyricist from the XML
          drawCredits: false,  // Hide credits from the XML
          drawPartNames: false,  // Hide part names from the XML
          drawingParameters: isPrintMode ? 'compact' : 'default',
          autoResize: true,
          followCursor: !isPrintMode,
          drawMeasureNumbers: true,
          drawTimeSignatures: true,
          drawMetronomeMarks: true,
          renderSingleHorizontalStaffline: false,
          coloringEnabled: false,
          // Responsive sizing
          zoom: isPrintMode ? 0.7 : 1.0,
        });

        osmdInstanceRef.current = osmd;

        // Load the MusicXML content
        let contentToLoad = musicXmlContent;
        
        // Check if this is stored as base64 MXL
        if (typeof musicXmlContent === 'string' && musicXmlContent.startsWith('mxl:base64:')) {
          // Extract and decode base64 MXL content
          const base64Data = musicXmlContent.substring('mxl:base64:'.length);
          contentToLoad = atob(base64Data);
        }
        
        await osmd.load(contentToLoad);
        osmd.render();
        
        // Force a re-render after a short delay to ensure visibility
        setTimeout(() => {
          if (osmdInstanceRef.current && osmdContainerRef.current) {
            osmdInstanceRef.current.render();
          }
        }, 200);

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing OSMD:', err);
        // More specific error message based on the error type
        if (err.message && err.message.includes('MXL')) {
          setError('Failed to load MXL file. The file may be corrupted or in an unsupported format.');
        } else if (err.message && err.message.includes('XML')) {
          setError('Failed to load MusicXML. Please check the file format.');
        } else {
          setError('Failed to load music notation. Please try a different file.');
        }
        setIsLoading(false);
      }
    };

    initializeOSMD();

    // Cleanup function
    return () => {
      if (osmdInstanceRef.current) {
        osmdInstanceRef.current.clear();
      }
    };
  }, [musicXmlContent, isVisible, isPrintMode]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (osmdInstanceRef.current && isVisible && musicXmlContent) {
        try {
          osmdInstanceRef.current.render();
        } catch (err) {
          // Ignore render errors on resize if OSMD is not ready
          console.debug('OSMD resize render skipped:', err.message);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, musicXmlContent]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.xml') && !file.name.endsWith('.musicxml') && !file.name.endsWith('.mxl')) {
      setError('Please upload a valid MusicXML file (.xml, .musicxml, or .mxl)');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // For MXL files, read as binary string (as OSMD expects)
      if (file.name.endsWith('.mxl')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const binaryString = e.target.result;
          setMusicXmlContent(binaryString);
          setIsMxlFile(true);
          if (onNotationChange) {
            // Store as base64 for persistence
            const base64 = btoa(binaryString);
            onNotationChange(`mxl:base64:${base64}`);
          }
          setIsLoading(false);
        };
        reader.onerror = () => {
          setError('Failed to read MXL file');
          setIsLoading(false);
        };
        // OSMD expects binary string for MXL files
        reader.readAsBinaryString(file);
      } else {
        // Regular XML files can be read as text
        uploadedFileRef.current = null;
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          setMusicXmlContent(content);
          setIsMxlFile(false);
          if (onNotationChange) {
            onNotationChange(content);
          }
          setIsLoading(false);
        };
        reader.onerror = () => {
          setError('Failed to read file');
          setIsLoading(false);
        };
        reader.readAsText(file);
      }
    } catch (err) {
      setError('Failed to process file');
      console.error('File processing error:', err);
      setIsLoading(false);
    }
  };

  // Handle clear notation
  const handleClearNotation = () => {
    setMusicXmlContent('');
    setIsMxlFile(false);
    uploadedFileRef.current = null;
    if (onNotationChange) {
      onNotationChange('');
    }
    if (osmdInstanceRef.current) {
      osmdInstanceRef.current.clear();
    }
    setError(null);
  };

  // Handle paste MusicXML
  const handlePasteMusicXML = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text && text.includes('<?xml')) {
        setMusicXmlContent(text);
        if (onNotationChange) {
          onNotationChange(text);
        }
      } else {
        setError('Clipboard does not contain valid MusicXML');
      }
    } catch (err) {
      setError('Failed to read clipboard. Please paste manually.');
    }
  };

  // Toggle visibility
  const toggleVisibility = () => {
    setIsVisible(!isVisible);
    // Trigger re-render when becoming visible
    if (!isVisible && osmdInstanceRef.current && musicXmlContent) {
      setTimeout(() => {
        try {
          osmdInstanceRef.current.render();
        } catch (err) {
          console.debug('OSMD render on visibility toggle:', err.message);
        }
      }, 100);
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if ((isPrintMode || minimalMode) && !musicXmlContent) {
    return null; // Don't show empty notation in print mode or minimal mode
  }

  // Minimal mode - just show the notation without any UI
  if (minimalMode && musicXmlContent) {
    return (
      <div className={`notation-section ${className}`}>
        <div 
          ref={osmdContainerRef} 
          className="osmd-container"
          style={{ 
            minHeight: '200px',
            width: '100%'
          }}
        />
      </div>
    );
  }

  return (
    <div className={`notation-section ${className} ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}>
      {/* Header with controls */}
      {!isPrintMode && (
        <div className="mb-2 p-2 bg-gray-50 rounded-t-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <Music className="w-4 h-4 text-gray-600" />
              {isEditing ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="text"
                    value={localSectionRef}
                    onChange={(e) => {
                      setLocalSectionRef(e.target.value);
                      if (onSectionRefChange) onSectionRefChange(e.target.value);
                    }}
                    placeholder="Section (e.g., Intro, Verse 1)"
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                    style={{ width: '150px' }}
                  />
                  <input
                    type="text"
                    value={localTitle}
                    onChange={(e) => {
                      setLocalTitle(e.target.value);
                      if (onTitleChange) onTitleChange(e.target.value);
                    }}
                    placeholder="Title (e.g., Main Riff, Solo)"
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary flex-1"
                    style={{ maxWidth: '250px' }}
                  />
                </div>
              ) : (
                <span className="text-sm font-medium text-gray-700">
                  {localSectionRef && localTitle ? `${localSectionRef} - ${localTitle}` : 
                   localSectionRef || localTitle || 'Music Notation'}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {isEditing && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xml,.musicxml,.mxl"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 transition-colors flex items-center gap-1"
                  title="Upload MusicXML"
                >
                  <Upload className="w-3 h-3" />
                  Upload
                </button>
                
                <button
                  onClick={handlePasteMusicXML}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  title="Paste MusicXML from clipboard"
                >
                  Paste XML
                </button>
                
                {musicXmlContent && (
                  <button
                    onClick={handleClearNotation}
                    className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors flex items-center gap-1"
                    title="Clear notation"
                  >
                    <X className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </>
            )}
            
            <button
              onClick={toggleVisibility}
              className="p-1 text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title={isVisible ? 'Hide notation' : 'Show notation'}
            >
              {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-1 text-gray-600 hover:bg-gray-200 rounded transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Notation display area */}
      {isVisible && (
        <div className={`notation-container ${isFullscreen ? 'h-full overflow-auto p-8' : 'min-h-[200px]'} bg-white border border-gray-200 rounded-b-md`}>
          {isLoading && (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading notation...</div>
            </div>
          )}
          
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded m-2">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          {!musicXmlContent && !isLoading && isEditing && (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400">
              <Music className="w-8 h-8 mb-2" />
              <p className="text-sm">No notation added yet</p>
              <p className="text-xs mt-1">Upload a MusicXML file to display notation</p>
            </div>
          )}
          
          {/* OSMD container */}
          <div 
            ref={osmdContainerRef} 
            className="osmd-container p-4"
            style={{ 
              minHeight: isFullscreen ? '100%' : '200px',
              width: '100%',
              display: musicXmlContent && !isLoading && !error ? 'block' : 'none',
              position: 'relative'
            }}
          />
        </div>
      )}

      {/* Instructions for editing mode */}
      {isEditing && !musicXmlContent && isVisible && !isPrintMode && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs">
          <p className="text-blue-700 font-medium mb-1">How to add notation:</p>
          <ul className="text-blue-600 space-y-1 ml-4 list-disc">
            <li>Export your notation as MusicXML from your notation software (Sibelius, Finale, MuseScore, etc.)</li>
            <li>Click "Upload" to select the MusicXML file</li>
            <li>Or copy the MusicXML content and click "Paste XML"</li>
          </ul>
        </div>
      )}

      <style>{`
        .osmd-container {
          width: 100%;
          overflow-x: auto;
        }
        
        .osmd-container svg {
          max-width: 100%;
          height: auto;
        }
        
        @media print {
          .notation-section {
            page-break-inside: avoid;
          }
          
          .osmd-container {
            overflow: visible;
          }
        }
      `}</style>
    </div>
  );
};

export default NotationSection;